import React, { useEffect, useMemo, useRef } from "react";
import { CanceledError } from "axios";
import * as XLSX from "xlsx";

import CustomSection from "../../custom-general/custom-section";
import ExecutiveSummary from "../../custom-visual-inspection/executive-summary";
import PDFTablePage from "../../../themed/pdf/pdfTablePage";
import useProjectDetails from "../../../../hooks/useProjectDetails";

import { PAUTScanDataItem } from "../pautScanDataItems";
import { Column } from "../../../../utils/models";
import { getResultItemStyle } from "../../../../utils/data";
import { formatUniquePublicID, getChunkedData } from "../../../../utils/utilities";
import { base64Upload } from "../../../../services/fileUpload";
import { uploadProjectUrl } from "../../../../services/reportService";

type ScanItem = Pick<PAUTScanDataItem, 'weldNumber' | 'scanNumber' | 'result'>;
type NumberedScanItem = { srNo: number; minimumMeasuredThickness: number; } & ScanItem;

interface Props {
    executiveSummary: string;
    pautDataItems: ScanItem[];
}

function generateBase64String<T>(items: T[], shouldDownload: boolean) {
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(items);
    
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    if (shouldDownload) XLSX.writeFile(workbook, "Sumary.xlsx");

    const buffer = XLSX.write(workbook, { bookType: "xls", type: 'array' });
    const prefix = 'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,';
    const base64String = btoa(new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), ''));

    return prefix + base64String;
}

const CustomPAUTSummary: React.FC<Props> = ({ executiveSummary, pautDataItems }) => {
    const { projectDetails } = useProjectDetails();
    const documentRef = useRef<HTMLDivElement>(null);

    const dataItems = useMemo(() => {
        return pautDataItems.map((item, index) => ({ srNo: index + 1, ...item })) as unknown as NumberedScanItem[]
    }, [pautDataItems]);

    const itemsToExport = useMemo(() => {
        return dataItems.map((pautItem) => ({
            SERIAL_NUMBER: pautItem.srNo,
            WELD_NUMBER: pautItem.weldNumber,
            SCAN_NUMBER: pautItem.scanNumber,
            MINIMUM_MEASURED_THICKNESS: pautItem.minimumMeasuredThickness,
            RESULT: pautItem.result, 
        }));
    }, [dataItems]);

    useEffect(() => {
        const controller = new AbortController();

        async function exportAndSave() {
            if (!projectDetails) return;

            try {
                const base64String = generateBase64String(itemsToExport, !!projectDetails.projectUrl);
                const url = await base64Upload(formatUniquePublicID(projectDetails.serialNumber) + '_Scan_Summary.xlsx', base64String);

                if (url) await uploadProjectUrl(projectDetails._id, url, controller.signal);
            } catch (error) {
                if (error instanceof CanceledError) return;
            }
        }

        if (documentRef.current) {
            exportAndSave();
        }  

        return () => {
            controller.abort();
        }
    }, [projectDetails, itemsToExport]);

    return ( 
        <>
            <ExecutiveSummary summary={executiveSummary} />

            {getChunkedData(dataItems, 25).map((chunk) => (
                <CustomSection sectionTitle="Scan Summary">
                    <div className="p-1" ref={documentRef}>
                        <PDFTablePage data={chunk} columns={COLUMNS} />
                    </div>
                </CustomSection>
            ))}
        </>
     );
};

const COLUMNS: Column[] = [
    {
        path: 'srNo',
        label: 'Sr. No'
    },
    {
        path: 'weldNumber',
        label: 'WELD NUMBER'
    },
    {
        path: 'scanNumber',
        label: 'SCAN NUMBER'
    },
    {
        path: 'minimumMeasuredThickness',
        label: 'MINIMUM MEASURED THICKNESS'
    },
    {
        path: 'result',
        label: 'RESULT',
        style: (item) => getResultItemStyle(item),
    },
];
 
export default CustomPAUTSummary;